/**
 * Universal PDP - Pricing & Delivery Selection
 * Handles quantity selection, delivery frequency, pricing updates
 */

/**
 * Trigger Booster variant select (same as non-universal products) so Booster updates URL
 * Uses booster:variant:select event - Booster's selectSwatch handles URL via history.replaceState
 * Payload must match the variant shape Booster expects (from noscript[data-bstr-swatch]) so selectSwatch
 * does not throw and reaches the URL update.
 * @param {string} variantId - Shopify variant ID
 */
function triggerBoosterVariantSelect(variantId) {
  if (!variantId) return;
  const priceData = document.getElementById('pdp-price-data');
  const productId = priceData?.dataset?.productId;
  if (!productId) return;
  // Match Booster's expected variant shape so selectSwatch runs through to URL update (no throw)
  const selectedVariant = {
    id: variantId,
    available: true,
    isCardSwatches: false,
    price: '$0.00',
    sale: false,
    price_raw: 0,
    sale_raw: false,
    sku: ''
  };
  const payload = {
    selectedVariant,
    currentOptions: [],
    product: { id: productId }
  };
  if (typeof BoosterTheme !== 'undefined' && BoosterTheme.events) {
    BoosterTheme.events.trigger('booster:variant:select', payload);
  } else {
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variantId);
      window.history.replaceState({}, '', url);
    } catch (e) {}
  }
}
window.triggerBoosterVariantSelect = triggerBoosterVariantSelect;

/**
 * Get variant ID from URL (?variant=12345)
 * @returns {string|null}
 */
function getVariantIdFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    const v = params.get('variant');
    return v && v.trim() !== '' ? v : null;
  } catch (e) {
    return null;
  }
}

/**
 * Get quantity and flavor for a variant ID from pdp-all-variants-data (for URL → UI sync on load)
 * @param {string} variantId
 * @returns {{ quantity: string, flavor: string }|null}
 */
function getVariantInfoFromVariantId(variantId) {
  if (!variantId) return null;
  const el = document.getElementById('pdp-all-variants-data');
  if (!el) return null;
  try {
    const data = JSON.parse(el.textContent);
    const variant = data.variants && data.variants.find(function(v) { return String(v.id) === String(variantId); });
    if (!variant) return null;
    const qty = ['1', '3', '6'].find(function(q) {
      const o1 = (variant.option1 || '').toString().trim().toLowerCase();
      const o2 = (variant.option2 || '').toString().trim().toLowerCase();
      const title = (variant.title || '').toLowerCase();
      return o1 === q || o2 === q ||
        o1.indexOf(q) === 0 || o2.indexOf(q) === 0 ||
        title.indexOf(q + ' month') !== -1 || title.indexOf(q + ' tub') !== -1 || title.indexOf(q + '-pack') !== -1;
    });
    var flavor = '';
    if (variant.option1 && variant.option2) {
      flavor = qty === variant.option1 || variant.option1 === '1' || variant.option1 === '3' || variant.option1 === '6' ? (variant.option2 || '') : (variant.option1 || '');
    } else {
      flavor = variant.option1 || variant.option2 || '';
    }
    if ((!flavor || flavor === '1' || flavor === '3' || flavor === '6') && variant.title) {
      var t = variant.title;
      if (t.indexOf('/') !== -1) flavor = t.split('/')[0].trim();
      else if (t.indexOf('-') !== -1) flavor = t.split('-')[0].trim();
      else flavor = t.trim();
    }
    return { quantity: qty || '3', flavor: flavor || '' };
  } catch (e) {
    return null;
  }
}
window.getVariantIdFromUrl = getVariantIdFromUrl;
window.getVariantInfoFromVariantId = getVariantInfoFromVariantId;

document.addEventListener('DOMContentLoaded', function() {
  console.log('🚀 Universal PDP Pricing initialized');
  
  // EMERGENCY FALLBACK: Always remove loader overlay (it blocks clicks even when buy box is visible)
  setTimeout(function() {
    forceBuyBoxInteractive();
  }, 5000);
  
  // Initialize Purchase Option Selection
  initializePurchaseOptions();
  
  // Initialize Delivery Display
  initializeDeliveryDisplay();
  
  // Initialize One-Time Purchase Price
  updateOneTimePrice('3'); // Default to 3 bottles
  
  // Initialize Subscription Discount Badge
  updateSubscriptionDiscountBadge('3'); // Default to 3 bottles
  
  // Set delivery display for default quantity (3 bottles)
  updateDeliverySelection('3');
  
  // Wait for Skio to Load (which will trigger dynamic plan discovery)
  waitForSkioToLoad();
  
  // Initialize Variant Selector (for non-Skio products)
  initializeVariantSelector();

  // Listen for dynamic plan updates
  document.addEventListener('skioDynamicPlansUpdated', function() {
    console.log('🔄 Skio dynamic plans updated, refreshing UI...');
    refreshDynamicUI();
  });
});

/**
 * Refresh UI Elements with Dynamic Plan Data
 */
function refreshDynamicUI() {
  // Refresh delivery options
  const activeQuantity = document.querySelector('.pdp-details__selection-option--active[data-quantity]');
  const quantity = activeQuantity ? activeQuantity.dataset.quantity : '3';
  
  // Update delivery selection for current quantity
  updateDeliverySelection(quantity);
  
  // Update pricing displays
  updateSubscriptionDiscountBadge(quantity);
  
  // Update add to cart button
  updateAddToCartButton();
  
  console.log('✅ Dynamic UI refresh complete');
}

/**
 * Initialize Purchase Options (Subscription vs One-Time)
 */
function initializePurchaseOptions() {
  const selectionOptions = document.querySelectorAll('.pdp-details__selection-option');
  const subscriptionContainer = document.querySelector('.pdp-details__subscription-container');
  const oneTimeContainer = document.querySelector('#one-time-option');
  const selectedQuantitySpan = document.querySelector('#selectedQuantity');
  
  // Set subscription as active by default
  if (subscriptionContainer) {
    subscriptionContainer.classList.add('pdp-details__subscription-container--active');
    subscriptionContainer.classList.remove('pdp-details__subscription-container--collapsed');
  }
  
  if (oneTimeContainer) {
    oneTimeContainer.classList.remove('pdp-details__one-time-container--active');
  }
  
  // Prefer quantity from URL variant so shared links open with correct selection (e.g. Vanilla 1 month)
  let defaultQuantityButton = null;
  let defaultQuantity = '3';
  var urlVariantId = getVariantIdFromUrl();
  if (urlVariantId) {
    var urlVariantInfo = getVariantInfoFromVariantId(urlVariantId);
    if (urlVariantInfo && urlVariantInfo.quantity) {
      defaultQuantity = urlVariantInfo.quantity;
      defaultQuantityButton = document.querySelector('[data-quantity="' + defaultQuantity + '"][data-available="true"]');
    }
  }
  if (!defaultQuantityButton) {
    defaultQuantityButton = document.querySelector('[data-quantity="3"][data-available="true"]');
    defaultQuantity = '3';
  }
  if (!defaultQuantityButton) {
    defaultQuantityButton = document.querySelector('[data-quantity="1"][data-available="true"]');
    defaultQuantity = '1';
  }
  if (!defaultQuantityButton) {
    defaultQuantityButton = document.querySelector('[data-quantity="6"][data-available="true"]');
    defaultQuantity = '6';
  }
  
  if (defaultQuantityButton) {
    defaultQuantityButton.classList.add('pdp-details__selection-option--active');
    updateMainPricing(defaultQuantity);
    updatePricePerDay(defaultQuantity);
    // Ensure Skio selected variant matches default quantity on initial render
    if (typeof dispatchVariantChangeToSkio === 'function') {
      dispatchVariantChangeToSkio(defaultQuantity);
    }
    const variantId = (window.getSelectedFlavor && window.findVariantByQuantityAndFlavor)
      ? window.findVariantByQuantityAndFlavor(defaultQuantity, window.getSelectedFlavor())
      : defaultQuantityButton.dataset.variantId;
    if (variantId) triggerBoosterVariantSelect(variantId);
  }
  
  // Handle Subscription Container Click
  if (subscriptionContainer) {
    subscriptionContainer.addEventListener('click', function(e) {
      e.preventDefault();
      e.stopPropagation();
      
      if (oneTimeContainer) {
        oneTimeContainer.classList.remove('pdp-details__one-time-container--active');
        // Hide the one-time purchase text when subscription is selected
        const oneTimePurchaseText = oneTimeContainer.querySelector('.pdp-details__one-time-purchase-text');
        if (oneTimePurchaseText) {
          oneTimePurchaseText.classList.remove('pdp-details__one-time-purchase-text--visible');
        }
      }
      
      this.classList.add('pdp-details__subscription-container--active');
      this.classList.remove('pdp-details__subscription-container--collapsed');
      
      // Update the selected text dynamically - target the frequency selection specifically
      const purchaseSection = document.querySelector('[data-type="purchase"]');
      const selectedText = purchaseSection ? purchaseSection.querySelector('.pdp-details__selection-selected') : null;
      if (selectedText) {
        selectedText.textContent = 'Subscribe & Save';
      }
      
      updateAddToCartButton();
      
      const activeQuantity = document.querySelector('.pdp-details__selection-option--active[data-quantity]');
      if (activeQuantity) {
        updateMainPricing(activeQuantity.dataset.quantity);
        updateSubscriptionDiscountBadge(activeQuantity.dataset.quantity);
        updatePricePerDay(activeQuantity.dataset.quantity);
      }
    });
  }
  
  // Handle One-Time Container Click
  if (oneTimeContainer) {
    oneTimeContainer.addEventListener('click', function(e) {
      e.preventDefault();
      e.stopPropagation();
      
      if (subscriptionContainer) {
        subscriptionContainer.classList.remove('pdp-details__subscription-container--active');
        subscriptionContainer.classList.add('pdp-details__subscription-container--collapsed');
      }
      
      this.classList.add('pdp-details__one-time-container--active');
      
      // Show the one-time purchase text block (only once - stays visible after first click)
      const oneTimePurchaseText = this.querySelector('.pdp-details__one-time-purchase-text');
      if (oneTimePurchaseText) {
        oneTimePurchaseText.classList.add('pdp-details__one-time-purchase-text--visible');
      }
      
      // Update the selected text dynamically - target the frequency selection specifically
      const purchaseSection = document.querySelector('[data-type="purchase"]');
      const selectedText = purchaseSection ? purchaseSection.querySelector('.pdp-details__selection-selected') : null;
      if (selectedText) {
        selectedText.textContent = 'One-time Purchase';
      }
      
      updateAddToCartButton();
      
      const activeQuantity = document.querySelector('.pdp-details__selection-option--active[data-quantity]');
      if (activeQuantity) {
        updateMainPricing(activeQuantity.dataset.quantity);
        updateSubscriptionDiscountBadge(activeQuantity.dataset.quantity);
        updatePricePerDay(activeQuantity.dataset.quantity);
      }
    });
  }
  
  // Handle Quantity Selection
  selectionOptions.forEach(option => {
    option.addEventListener('click', function(e) {
      e.preventDefault();
      e.stopPropagation();
      
      if (this.disabled || this.dataset.available === 'false') {
        return;
      }
      
      const selectionType = this.dataset.type;
      const sectionType = this.closest('.pdp-details__selection-options').dataset.type;
      if (sectionType === 'quantity') {
        const quantityButtons = document.querySelectorAll('.pdp-details__selection-options[data-type="quantity"] .pdp-details__selection-option');
        quantityButtons.forEach(opt => opt.classList.remove('pdp-details__selection-option--active'));
        this.classList.add('pdp-details__selection-option--active');
        
        // Switch back to subscription when quantity changes
        if (subscriptionContainer && oneTimeContainer) {
          subscriptionContainer.classList.add('pdp-details__subscription-container--active');
          subscriptionContainer.classList.remove('pdp-details__subscription-container--collapsed');
          oneTimeContainer.classList.remove('pdp-details__one-time-container--active');
          // Hide the one-time purchase text when switching back to subscription
          const oneTimePurchaseText = oneTimeContainer.querySelector('.pdp-details__one-time-purchase-text');
          if (oneTimePurchaseText) {
            oneTimePurchaseText.classList.remove('pdp-details__one-time-purchase-text--visible');
          }
          
          // Update the selected text dynamically - target the frequency selection specifically
          const purchaseSection = document.querySelector('[data-type="purchase"]');
          const selectedText = purchaseSection ? purchaseSection.querySelector('.pdp-details__selection-selected') : null;
          if (selectedText) {
            selectedText.textContent = 'Subscribe & Save';
          }
          
          updateAddToCartButton();
        }
        
        const quantity = this.dataset.quantity;
        const quantityText = this.querySelector('.pdp-details__selection-text').textContent;
        if (selectedQuantitySpan) {
          selectedQuantitySpan.textContent = quantityText;
        }
        updateDeliverySelection(quantity);
        updateMainPricing(quantity);
        updateOneTimePrice(quantity);
        updateSubscriptionDiscountBadge(quantity);
        updatePricePerDay(quantity);
        
        // Dispatch variant change to Skio
        if (typeof dispatchVariantChangeToSkio === 'function') {
          dispatchVariantChangeToSkio(quantity);
        }
        const variantId = (window.getSelectedFlavor && window.findVariantByQuantityAndFlavor)
          ? window.findVariantByQuantityAndFlavor(quantity, window.getSelectedFlavor())
          : this.dataset.variantId;
        if (variantId) triggerBoosterVariantSelect(variantId);
      }
      
      updateAddToCartButton();
    });
  });
  
  // Initialize Delivery Dropdown
  initializeDeliveryDropdown();
}

/**
 * Skio selling plan pricing policy % (Percentage off in admin — not calculated from prices).
 */
function getSkioPlanPolicyPctFromAdjustments(priceAdjustments) {
  if (!priceAdjustments || !priceAdjustments.length) return 0;
  const pctAdj = priceAdjustments.find((adj) => adj.value_type === 'percentage');
  return pctAdj ? Number(pctAdj.value) : 0;
}

function updateSubscribeBenefitDiscountText() {
  const el = document.querySelector('[data-subscribe-benefit-discount]');
  if (!el) return;
  const prefix = el.dataset.benefitPrefix || 'Save ';
  const suffix = el.dataset.benefitSuffix || ' off subscriptions';
  const fallback = el.dataset.benefitFallback || '';
  const activeOption = document.querySelector('.pdp-details__delivery-option--active');
  const pct = Number(activeOption?.dataset.subscribePolicyPct || 0);
  if (pct > 0) {
    el.textContent = prefix + ' ' + pct + '%' + suffix;
  } else {
    el.textContent = fallback;
  }
}
window.updateSubscribeBenefitDiscountText = updateSubscribeBenefitDiscountText;

/**
 * Initialize Delivery Dropdown
 */
function initializeDeliveryDropdown() {
  const deliverySelector = document.querySelector('.pdp-details__delivery-selector');
  const deliveryDropdown = document.querySelector('.pdp-details__delivery-dropdown');
  const selectedDeliveryFrequency = document.querySelector('#selectedDeliveryFrequency');
  const deliveryOptions = document.querySelectorAll('.pdp-details__delivery-option');
  
  // Toggle dropdown on click
  if (deliverySelector) {
    deliverySelector.addEventListener('click', function(e) {
      e.preventDefault();
      e.stopPropagation();
      
      if (deliveryDropdown) {
        deliveryDropdown.classList.toggle('pdp-details__delivery-dropdown--open');
        deliverySelector.classList.toggle('pdp-details__delivery-selector--open');
      }
    });
  }
  
  // Delivery options: event delegation (works after dynamic dropdown rebuild)
  function handleDeliveryOptionClick(e) {
    const option = e.target.closest('.pdp-details__delivery-option');
    if (!option || !deliveryDropdown || !deliveryDropdown.contains(option)) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    
    const currentDeliveryOptions = document.querySelectorAll('.pdp-details__delivery-option');
    currentDeliveryOptions.forEach(opt => opt.classList.remove('pdp-details__delivery-option--active'));
    option.classList.add('pdp-details__delivery-option--active');
    
    const planName = option.dataset.planName;
    const price = option.dataset.price;
    const frequency = option.dataset.frequency;
    const badge = option.dataset.badge || '';
    const quantity = option.dataset.quantity;
    
    updateDeliveryDisplay(frequency, planName, badge);
    updateSubscribeBenefitDiscountText();
    
    const subscriptionCurrentPrice = document.querySelector('#subscriptionCurrentPrice');
    const subscriptionOriginalPrice = document.querySelector('#subscriptionOriginalPrice');
    
    if (subscriptionCurrentPrice && price) {
      subscriptionCurrentPrice.textContent = price;
    }
    
    // Update original price with dynamic data if available
    if (subscriptionOriginalPrice) {
      if (window.SkioPlansCache && window.SkioPlansCache.initialized && quantity) {
        const plan = window.getDynamicPlanForQuantity ? window.getDynamicPlanForQuantity(quantity) : null;
        if (plan) {
          subscriptionOriginalPrice.textContent = formatPrice(plan.compareAtPrice);
        }
      } else {
        // Fallback to static data
        const variantData = document.getElementById('variantPriceData');
        let originalPriceInCents = 0;
        if (frequency === '1-month' || frequency === '1-months') {
          originalPriceInCents = parseInt(variantData.dataset.oneCompare);
        } else if (frequency === '3-months') {
          originalPriceInCents = parseInt(variantData.dataset.threeCompare);
        } else if (frequency === '6-months') {
          originalPriceInCents = parseInt(variantData.dataset.sixCompare);
        }
        subscriptionOriginalPrice.textContent = formatPrice(originalPriceInCents);
      }
    }

    // Update swatch selection and sync quantity
    if (quantity) {
      updateSwatchSelectionFromQuantity(quantity);
    } else {
      updateSwatchSelectionFromFrequency(frequency);
    }
    
    updateOneTimePriceFromFrequency(frequency);
    
    const activeQuantity = document.querySelector('.pdp-details__selection-option--active[data-quantity]');
    if (activeQuantity) {
      updateSubscriptionDiscountBadge(activeQuantity.dataset.quantity);
      updatePricePerDay(activeQuantity.dataset.quantity);
      const qty = activeQuantity.dataset.quantity;
      const variantId = (window.getSelectedFlavor && window.findVariantByQuantityAndFlavor)
        ? window.findVariantByQuantityAndFlavor(qty, window.getSelectedFlavor())
        : activeQuantity.dataset.variantId;
      if (variantId && window.triggerBoosterVariantSelect) {
        window.triggerBoosterVariantSelect(variantId);
      }
    }
    
    if (deliveryDropdown) {
      deliveryDropdown.classList.remove('pdp-details__delivery-dropdown--open');
      deliverySelector.classList.remove('pdp-details__delivery-selector--open');
    }
    updateAddToCartButton();
  }

  if (deliveryDropdown) {
    deliveryDropdown.addEventListener('click', handleDeliveryOptionClick);
  }

  updateSubscribeBenefitDiscountText();

  /**
   * Update Swatch Selection from Quantity
   */
  function updateSwatchSelectionFromQuantity(quantity) {
    const selectionOptions = document.querySelectorAll('.pdp-details__selection-option[data-type="quantity"]');
    selectionOptions.forEach(option => {
      if (option.dataset.quantity === quantity) {
        selectionOptions.forEach(opt => opt.classList.remove('pdp-details__selection-option--active'));
        option.classList.add('pdp-details__selection-option--active');
        
        // Keep Skio variant in sync when delivery drives quantity change
        if (typeof dispatchVariantChangeToSkio === 'function') {
          dispatchVariantChangeToSkio(quantity);
        }
      }
    });
  }
  
  // Close dropdown when clicking outside
  document.addEventListener('click', function(e) {
    if (deliveryDropdown && deliverySelector && !deliverySelector.contains(e.target)) {
      deliveryDropdown.classList.remove('pdp-details__delivery-dropdown--open');
      deliverySelector.classList.remove('pdp-details__delivery-selector--open');
    }
  });
}

/**
 * Update One-Time Purchase Price
 */
function updateOneTimePrice(quantity) {
  const oneTimePriceElement = document.querySelector('#oneTimePrice');
  const oneTimeComparePriceElement = document.querySelector('#oneTimeComparePrice');
  const oneTimeDiscountBadge = document.querySelector('#oneTimeDiscountBadge');
  if (!oneTimePriceElement) return;
  
  const priceData = getPriceData();
  const variantData = document.getElementById('variantPriceData');
  let price = '';
  let comparePrice = 0;
  
  if (quantity === '1') {
    price = priceData.oneBottlePrice;
    if (variantData) {
      comparePrice = parseInt(variantData.dataset.oneCompare);
    }
  } else if (quantity === '3') {
    price = priceData.threeBottlePrice;
    if (variantData) {
      comparePrice = parseInt(variantData.dataset.threeCompare);
    }
  } else if (quantity === '6') {
    price = priceData.sixBottlePrice;
    if (variantData) {
      comparePrice = parseInt(variantData.dataset.sixCompare);
    }
  }
  
  if (price) {
    oneTimePriceElement.textContent = price;
  }
  
  // Update compare-at price
  const oneTimePriceInCents = parseInt(price.replace(/[^0-9]/g, ''));
  if (oneTimeComparePriceElement && comparePrice && comparePrice > oneTimePriceInCents) {
    oneTimeComparePriceElement.textContent = formatPrice(comparePrice);
    oneTimeComparePriceElement.style.display = '';
    
    // Update discount badge
    if (oneTimeDiscountBadge) {
      if (comparePrice > oneTimePriceInCents) {
        const calculatedDiscount = Math.round(((comparePrice - oneTimePriceInCents) / comparePrice) * 100);
        oneTimeDiscountBadge.textContent = calculatedDiscount + '% off';
        oneTimeDiscountBadge.style.display = '';
      } else {
        oneTimeDiscountBadge.style.display = 'none';
      }
    }
  } else {
    if (oneTimeComparePriceElement) {
      oneTimeComparePriceElement.style.display = 'none';
    }
    if (oneTimeDiscountBadge) {
      oneTimeDiscountBadge.style.display = 'none';
    }
  }
}

/**
 * Format Price from Cents
 */
function formatPrice(priceInCents) {
  const currencyCode = getCurrencyCode();
  return (priceInCents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}

/**
 * Update Main Pricing Display
 */
function updateMainPricing(quantity) {
  const variantData = document.getElementById('variantPriceData');
  if (!variantData) return;
  
  const subscriptionContainer = document.querySelector('.pdp-details__subscription-container');
  const oneTimeContainer = document.querySelector('#one-time-option');
  const isSubscriptionActive = subscriptionContainer && subscriptionContainer.classList.contains('pdp-details__subscription-container--active');
  const isOneTimeActive = oneTimeContainer && oneTimeContainer.classList.contains('pdp-details__one-time-container--active');
  
  let price, comparePrice;
  
  if (quantity === '1') {
    if (isOneTimeActive) {
      price = parseInt(variantData.dataset.onePriceOnetime);
      comparePrice = parseInt(variantData.dataset.oneCompare);
    } else {
      price = parseInt(variantData.dataset.onePriceSubscription);
      comparePrice = parseInt(variantData.dataset.oneCompare);
    }
  } else if (quantity === '3') {
    if (isOneTimeActive) {
      price = parseInt(variantData.dataset.threePriceOnetime);
      comparePrice = parseInt(variantData.dataset.threeCompare);
    } else {
      price = parseInt(variantData.dataset.threePriceSubscription);
      comparePrice = parseInt(variantData.dataset.threeCompare);
    }
  } else if (quantity === '6') {
    if (isOneTimeActive) {
      price = parseInt(variantData.dataset.sixPriceOnetime);
      comparePrice = parseInt(variantData.dataset.sixCompare);
    } else {
      price = parseInt(variantData.dataset.sixPriceSubscription);
      comparePrice = parseInt(variantData.dataset.sixCompare);
    }
  }
  
  const priceGroup = document.getElementById('priceGroup');
  const salePrice = document.getElementById('salePrice');
  const compareElement = document.getElementById('comparePrice');
  const discountBadge = document.getElementById('discountBadge');
  const discountText = document.getElementById('discountText');
  const regularPrice = document.getElementById('regularPrice');
  
  if (!priceGroup) return;
  if (comparePrice && !isNaN(comparePrice) && comparePrice > price) {
    // Always calculate discount from the CURRENT price vs compare-at
    // This makes the badge update correctly when switching between
    // subscription and one-time purchase.
    let discountPercentage = Math.round(((comparePrice - price) / comparePrice) * 100);
    
    if (salePrice) {
      salePrice.textContent = formatPrice(price);
      salePrice.style.display = '';
    } else {
      const newSalePrice = document.createElement('span');
      newSalePrice.className = 'pdp-details__price pdp-details__price--sale';
      newSalePrice.id = 'salePrice';
      newSalePrice.textContent = formatPrice(price);
      priceGroup.insertBefore(newSalePrice, priceGroup.firstChild);
    }
    
    if (compareElement) {
      compareElement.textContent = formatPrice(comparePrice);
      compareElement.style.display = '';
    } else {
      const newComparePrice = document.createElement('span');
      newComparePrice.className = 'pdp-details__price pdp-details__price--compare';
      newComparePrice.id = 'comparePrice';
      newComparePrice.textContent = formatPrice(comparePrice);
      const targetSalePrice = document.getElementById('salePrice');
      if (targetSalePrice && targetSalePrice.nextSibling) {
        priceGroup.insertBefore(newComparePrice, targetSalePrice.nextSibling);
      } else {
        priceGroup.appendChild(newComparePrice);
      }
    }
    
    if (discountText) {
      discountText.textContent = discountPercentage + '% off';
      if (discountBadge) discountBadge.style.display = '';
    } else if (discountBadge) {
      const newDiscountText = document.createElement('span');
      newDiscountText.className = 'pdp-details__discount-text';
      newDiscountText.id = 'discountText';
      newDiscountText.textContent = discountPercentage + '% off';
      discountBadge.appendChild(newDiscountText);
      discountBadge.style.display = '';
    } else {
      const newBadge = document.createElement('div');
      newBadge.className = 'pdp-details__discount-badge';
      newBadge.id = 'discountBadge';
      const newDiscountText = document.createElement('span');
      newDiscountText.className = 'pdp-details__discount-text';
      newDiscountText.id = 'discountText';
      newDiscountText.textContent = discountPercentage + '% off';
      newBadge.appendChild(newDiscountText);
      priceGroup.appendChild(newBadge);
    }
    
    // Update subscription discount badge separately based on subscription price
    updateSubscriptionDiscountBadge(quantity);
    
    if (regularPrice) {
      regularPrice.style.display = 'none';
    }
  } else {
    if (regularPrice) {
      regularPrice.textContent = formatPrice(price);
      regularPrice.style.display = '';
    } else {
      const newRegularPrice = document.createElement('span');
      newRegularPrice.className = 'pdp-details__price';
      newRegularPrice.id = 'regularPrice';
      newRegularPrice.textContent = formatPrice(price);
      priceGroup.appendChild(newRegularPrice);
    }
    
    if (salePrice) salePrice.style.display = 'none';
    if (compareElement) compareElement.style.display = 'none';
    if (discountBadge) discountBadge.style.display = 'none';
  }
}

/**
 * Update Subscription Discount Badge (Enhanced with Dynamic Plans)
 */
function updateSubscriptionDiscountBadge(quantity) {
  const subscriptionDiscountBadge = document.getElementById('subscriptionDiscountBadge');
  if (!subscriptionDiscountBadge) return;
  
  // Use dynamic plan data if available
  if (window.SkioPlansCache && window.SkioPlansCache.initialized) {
    const plan = window.getDynamicPlanForQuantity ? window.getDynamicPlanForQuantity(quantity) : null;
    if (plan && plan.discountPercentage > 0) {
      subscriptionDiscountBadge.textContent = `${plan.discountPercentage}% off`;
      subscriptionDiscountBadge.style.display = '';
      return;
    } else if (plan) {
      subscriptionDiscountBadge.style.display = 'none';
      return;
    }
  }
  
  // Fallback to static data
  const variantData = document.getElementById('variantPriceData');
  if (!variantData) return;
  
  let subscriptionPrice = 0;
  let comparePrice = 0;
  
  // Get subscription price and compare price for selected quantity
  if (quantity === '1') {
    subscriptionPrice = parseInt(variantData.dataset.onePriceSubscription);
    comparePrice = parseInt(variantData.dataset.oneCompare);
  } else if (quantity === '3') {
    subscriptionPrice = parseInt(variantData.dataset.threePriceSubscription);
    comparePrice = parseInt(variantData.dataset.threeCompare);
  } else if (quantity === '6') {
    subscriptionPrice = parseInt(variantData.dataset.sixPriceSubscription);
    comparePrice = parseInt(variantData.dataset.sixCompare);
  }
  
  // Calculate discount percentage for subscription
  if (comparePrice && !isNaN(comparePrice) && comparePrice > subscriptionPrice) {
    const discountPercentage = Math.round(((comparePrice - subscriptionPrice) / comparePrice) * 100);
    subscriptionDiscountBadge.textContent = discountPercentage + '% off';
    subscriptionDiscountBadge.style.display = '';
  } else {
    subscriptionDiscountBadge.style.display = 'none';
  }
}

/**
 * Update Price Per Day
 */
function updatePricePerDay(quantity) {
  const pricePerDayElement = document.querySelector('.pdp-details__price-per-day');
  if (!pricePerDayElement) return;
  
  const subscriptionContainer = document.querySelector('.pdp-details__subscription-container');
  const isSubscriptionActive = subscriptionContainer && subscriptionContainer.classList.contains('pdp-details__subscription-container--active');
  
  const quantityNum = parseInt(quantity);
  const totalDays = quantityNum * 30;
  
  const priceData = getPriceDataInCents();
  let priceInCents = 0;
  
  if (isSubscriptionActive) {
    if (quantity === '1') {
      priceInCents = priceData.monthlyPriceInCents;
    } else if (quantity === '3') {
      priceInCents = priceData.quarterlyPriceInCents;
    } else if (quantity === '6') {
      priceInCents = priceData.semiAnnualPriceInCents;
    }
  } else {
    if (quantity === '1') {
      priceInCents = priceData.oneBottlePriceInCents;
    } else if (quantity === '3') {
      priceInCents = priceData.threeBottlePriceInCents;
    } else if (quantity === '6') {
      priceInCents = priceData.sixBottlePriceInCents;
    }
  }
  
  const pricePerDay = priceInCents / totalDays;
  const formattedPrice = formatPrice(pricePerDay);
  
  pricePerDayElement.textContent = '(' + formattedPrice + '/day)';
}

/**
 * Get Short Display Name for Frequency
 */
function getShortDisplayName(frequency, planName) {
  // Just use the actual plan name from SKIO - no hardcoding
  return planName || frequency;
}

/**
 * Update Delivery Display with Badge
 */
function updateDeliveryDisplay(frequency, planName, badge = '') {
  const selectedDeliveryFrequency = document.querySelector('#selectedDeliveryFrequency');
  if (!selectedDeliveryFrequency) return;
  
  selectedDeliveryFrequency.innerHTML = '';
  
  const textSpan = document.createElement('span');
  textSpan.textContent = getShortDisplayName(frequency, planName);
  selectedDeliveryFrequency.appendChild(textSpan);
  
  if (badge && badge.trim() !== '') {
    const badgeElement = document.createElement('span');
    badgeElement.className = 'pdp-details__delivery-badge';
    badgeElement.textContent = badge;
    selectedDeliveryFrequency.appendChild(badgeElement);
  }
}

/**
 * Update Swatch Selection from Frequency
 */
function updateSwatchSelectionFromFrequency(frequency) {
  let targetQuantity = '3';
  
  if (frequency === '1-month') {
    targetQuantity = '1';
  } else if (frequency === '3-months') {
    targetQuantity = '3';
  } else if (frequency === '6-months') {
    targetQuantity = '6';
  }
  
  const selectionOptions = document.querySelectorAll('.pdp-details__selection-option[data-type="quantity"]');
  selectionOptions.forEach(option => {
    if (option.dataset.quantity === targetQuantity) {
      selectionOptions.forEach(opt => opt.classList.remove('pdp-details__selection-option--active'));
      option.classList.add('pdp-details__selection-option--active');
      // Keep Skio variant in sync when frequency drives quantity change
      if (typeof dispatchVariantChangeToSkio === 'function') {
        dispatchVariantChangeToSkio(targetQuantity);
      }
    }
  });
}

/**
 * Update One-Time Price from Frequency
 */
function updateOneTimePriceFromFrequency(frequency) {
  let targetQuantity = '3';
  
  if (frequency === '1-month') {
    targetQuantity = '1';
  } else if (frequency === '3-months') {
    targetQuantity = '3';
  } else if (frequency === '6-months') {
    targetQuantity = '6';
  }
  
  updateOneTimePrice(targetQuantity);
}

/**
 * Update Delivery Selection Based on Quantity (Enhanced with Dynamic Plans)
 */
function updateDeliverySelection(quantity) {
  // Use dynamic plan data if available, fallback to static logic
  if (window.SkioPlansCache && window.SkioPlansCache.initialized) {
    updateDeliverySelectionDynamic(quantity);
    return;
  }

  // Original static logic as fallback
  let targetFrequency = '3-months';
  
  if (quantity === '1') {
    targetFrequency = '1-month';
  } else if (quantity === '3') {
    targetFrequency = '3-months';
  } else if (quantity === '6') {
    targetFrequency = '6-months';
  }
  
  const deliveryOptions = document.querySelectorAll('.pdp-details__delivery-option');
  deliveryOptions.forEach(option => {
    if (option.dataset.frequency === targetFrequency || option.dataset.quantity === quantity) {
      deliveryOptions.forEach(opt => opt.classList.remove('pdp-details__delivery-option--active'));
      option.classList.add('pdp-details__delivery-option--active');
      
      const frequency = option.dataset.frequency;
      const badge = option.dataset.badge || '';
      const price = option.dataset.price;
      
      updateDeliveryDisplay(frequency, option.dataset.planName, badge);
      updateSubscribeBenefitDiscountText();
      
      const subscriptionCurrentPrice = document.querySelector('#subscriptionCurrentPrice');
      const subscriptionOriginalPrice = document.querySelector('#subscriptionOriginalPrice');
      if (subscriptionCurrentPrice && price) {
        subscriptionCurrentPrice.textContent = price;
      }
      
      if (subscriptionOriginalPrice) {
        const variantData = document.getElementById('variantPriceData');
        let originalPriceInCents = 0;
        if (quantity === '1') {
          originalPriceInCents = parseInt(variantData.dataset.oneCompare);
        } else if (quantity === '3') {
          originalPriceInCents = parseInt(variantData.dataset.threeCompare);
        } else if (quantity === '6') {
          originalPriceInCents = parseInt(variantData.dataset.sixCompare);
        }
        subscriptionOriginalPrice.textContent = formatPrice(originalPriceInCents);
      }
      
      // Update subscription discount badge when delivery selection changes
      updateSubscriptionDiscountBadge(quantity);

      setTimeout(() => {
        updateAddToCartButton();
      }, 100);
      
      return; // Exit after finding match
    }
  });
}

/**
 * Dynamic Delivery Selection Update
 */
function updateDeliverySelectionDynamic(quantity) {
  const plan = window.getDynamicPlanForQuantity ? window.getDynamicPlanForQuantity(quantity) : null;
  if (!plan) return;

  // Find and activate the correct option
  const deliveryOptions = document.querySelectorAll('.pdp-details__delivery-option');
  deliveryOptions.forEach(option => {
    if (option.dataset.quantity === quantity || option.dataset.planId === plan.id) {
      deliveryOptions.forEach(opt => opt.classList.remove('pdp-details__delivery-option--active'));
      option.classList.add('pdp-details__delivery-option--active');
      
      const frequency = option.dataset.frequency;
      const badge = option.dataset.badge || '';
      
      // Use the dropdown option's plan name (correct) instead of dynamic plan name
      updateDeliveryDisplay(frequency, option.dataset.planName, badge);
      updateSubscribeBenefitDiscountText();
      
      // Update pricing with dynamic data
      const subscriptionCurrentPrice = document.querySelector('#subscriptionCurrentPrice');
      const subscriptionOriginalPrice = document.querySelector('#subscriptionOriginalPrice');
      
      if (subscriptionCurrentPrice) {
        subscriptionCurrentPrice.textContent = formatPrice(plan.finalPrice);
      }
      
      if (subscriptionOriginalPrice) {
        subscriptionOriginalPrice.textContent = formatPrice(plan.compareAtPrice);
      }
      
      // Update discount badge with dynamic percentage
      const subscriptionDiscountBadge = document.getElementById('subscriptionDiscountBadge');
      if (subscriptionDiscountBadge && plan.discountPercentage > 0) {
        subscriptionDiscountBadge.textContent = `${plan.discountPercentage}% off`;
        subscriptionDiscountBadge.style.display = '';
      } else if (subscriptionDiscountBadge) {
        subscriptionDiscountBadge.style.display = 'none';
      }

      setTimeout(() => {
        updateAddToCartButton();
      }, 100);
      
      return;
    }
  });
}

/**
 * Initialize Delivery Display on Page Load
 */
function initializeDeliveryDisplay() {
  const activeOption = document.querySelector('.pdp-details__delivery-option--active');
  const selectedDeliveryFrequency = document.querySelector('#selectedDeliveryFrequency');
  
  if (activeOption && selectedDeliveryFrequency) {
    const planName = activeOption.dataset.planName;
    const price = activeOption.dataset.price;
    const frequency = activeOption.dataset.frequency;
    const badge = activeOption.dataset.badge || '';
    
    updateDeliveryDisplay(frequency, planName, badge);
    updateSubscribeBenefitDiscountText();
    
    const subscriptionCurrentPrice = document.querySelector('#subscriptionCurrentPrice');
    const subscriptionOriginalPrice = document.querySelector('#subscriptionOriginalPrice');
    
    if (subscriptionCurrentPrice && price) {
      subscriptionCurrentPrice.textContent = price;
    }
    
    if (subscriptionOriginalPrice) {
      const variantData = document.getElementById('variantPriceData');
      const originalPriceInCents = parseInt(variantData.dataset.threeCompare);
      subscriptionOriginalPrice.textContent = formatPrice(originalPriceInCents);
    }
  }
}

/**
 * Force buy box interactive — hide loader overlay (blocks all clicks when left on top)
 */
function forceBuyBoxInteractive() {
  const selectionLoader = document.querySelector('#selectionLoader');
  const selectionContent = document.querySelector('#selectionContent');

  if (selectionLoader) {
    selectionLoader.style.display = 'none';
    selectionLoader.style.pointerEvents = 'none';
    selectionLoader.setAttribute('aria-hidden', 'true');
  }
  if (selectionContent) {
    selectionContent.style.display = 'block';
    selectionContent.style.pointerEvents = 'auto';
    selectionContent.classList.remove('loading');
  }
}

/**
 * Wait for Skio to Complete Loading
 * IMPORTANT: Never hide #selectionContent — only toggle the loader overlay.
 * Hiding content + leaving loader on top = visible but unclickable buy box.
 */
function waitForSkioToLoad() {
  const selectionLoader = document.querySelector('#selectionLoader');
  const selectionContent = document.querySelector('#selectionContent');

  function showLoader() {
    if (selectionLoader) {
      selectionLoader.style.display = 'flex';
      selectionLoader.style.pointerEvents = 'auto';
      selectionLoader.removeAttribute('aria-hidden');
    }
  }

  function onSkioReady() {
    forceBuyBoxInteractive();

    if (typeof window.initializeSkioDynamicPlans === 'function') {
      window.initializeSkioDynamicPlans();
    }

    const activeQuantity = document.querySelector('.pdp-details__selection-option--active[data-quantity]');
    if (activeQuantity) {
      if (typeof dispatchVariantChangeToSkio === 'function') {
        dispatchVariantChangeToSkio(activeQuantity.dataset.quantity);
      }
      updateSubscriptionDiscountBadge(activeQuantity.dataset.quantity);
    }

    updateAddToCartButton();
  }

  if (!selectionContent) {
    return;
  }

  // Buy box always visible and clickable by default
  forceBuyBoxInteractive();

  const skioComponent = document.querySelector('skio-plan-picker');

  if (!skioComponent) {
    updateAddToCartButton();
    return;
  }

  function checkSkioLoaded() {
    if (!skioComponent.hasAttribute('product')) {
      return false;
    }
    try {
      const productData = JSON.parse(skioComponent.getAttribute('product'));
      const sellingPlans = productData.selling_plan_groups.filter(function (group) {
        return group.app_id === 'SKIO';
      });
      if (sellingPlans.length > 0) {
        onSkioReady();
        return true;
      }
    } catch (error) {
      console.error('Error checking Skio loaded state:', error);
      onSkioReady();
      return true;
    }
    return false;
  }

  if (checkSkioLoaded()) {
    return;
  }

  showLoader();

  const timeoutId = setTimeout(function () {
    observer.disconnect();
    onSkioReady();
  }, 3000);

  const observer = new MutationObserver(function () {
    if (checkSkioLoaded()) {
      clearTimeout(timeoutId);
      observer.disconnect();
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
}

/**
 * Update Add to Cart Button
 */
function updateAddToCartButton() {
  const subscriptionContainer = document.querySelector('.pdp-details__subscription-container');
  const oneTimeContainer = document.querySelector('#one-time-option');
  const skioComponent = document.querySelector('skio-plan-picker');
  
  // Update both buttons (desktop and mobile)
  const addToCartTextElements = document.querySelectorAll('#add-to-cart-text, #add-to-cart-text-mobile');
  
  if (!addToCartTextElements.length) {
    return;
  }
  
  // Non-Skio simple variant list only (exclude 1/3/6 quantity cards — they use subscription/one-time or main price nodes)
  if (!skioComponent) {
    const activeSimpleVariant = document.querySelector(
      '.pdp-details__selection-option--active[data-variant-id][data-type="quantity"]:not([data-quantity])'
    );
    if (activeSimpleVariant) {
      updateAddToCartButtonForVariant(activeSimpleVariant);
      return;
    }
    const activeQtyCard = document.querySelector('.pdp-details__selection-option--active[data-quantity]');
    if (activeQtyCard) {
      const saleEl = document.getElementById('salePrice');
      const regEl = document.getElementById('regularPrice');
      const mainPrice =
        (saleEl && saleEl.textContent.trim()) || (regEl && regEl.textContent.trim());
      if (mainPrice) {
        addToCartTextElements.forEach(function (textElement) {
          textElement.textContent = 'Add to Cart • ' + mainPrice;
        });
        return;
      }
    }
  }
  
  const isSubscriptionActive = subscriptionContainer && subscriptionContainer.classList.contains('pdp-details__subscription-container--active');
  const isOneTimeActive = oneTimeContainer && oneTimeContainer.classList.contains('pdp-details__one-time-container--active');
  
  let buttonText = 'Add to Cart';
  
  if (isSubscriptionActive) {
    const subscriptionCurrentPrice = document.querySelector('#subscriptionCurrentPrice');
    if (subscriptionCurrentPrice) {
      const price = subscriptionCurrentPrice.textContent.trim();
      buttonText = `Add to Cart • ${price}`;
    }
  } else if (isOneTimeActive) {
    const oneTimePrice = document.querySelector('#oneTimePrice');
    if (oneTimePrice) {
      const price = oneTimePrice.textContent.trim();
      buttonText = `Add to Cart • ${price}`;
    }
  } else {
    const subscriptionCurrentPrice = document.querySelector('#subscriptionCurrentPrice');
    if (subscriptionCurrentPrice) {
      const price = subscriptionCurrentPrice.textContent.trim();
      buttonText = `Add to Cart • ${price}`;
    }
  }
  
  // Update all button text elements
  addToCartTextElements.forEach(textElement => {
    textElement.textContent = buttonText;
  });
}

/**
 * Helper: Get Price Data from Data Attributes
 */
function getPriceData() {
  const priceDataElement = document.getElementById('pdp-price-data');
  if (!priceDataElement) return {};
  
  return {
    oneBottlePrice: priceDataElement.dataset.oneBottlePrice || '',
    threeBottlePrice: priceDataElement.dataset.threeBottlePrice || '',
    sixBottlePrice: priceDataElement.dataset.sixBottlePrice || ''
  };
}

/**
 * Helper: Get Price Data in Cents
 */
function getPriceDataInCents() {
  const priceDataElement = document.getElementById('pdp-price-data');
  if (!priceDataElement) return {};
  
  return {
    monthlyPriceInCents: parseInt(priceDataElement.dataset.monthlyPriceInCents) || 0,
    quarterlyPriceInCents: parseInt(priceDataElement.dataset.quarterlyPriceInCents) || 0,
    semiAnnualPriceInCents: parseInt(priceDataElement.dataset.semiAnnualPriceInCents) || 0,
    oneBottlePriceInCents: parseInt(priceDataElement.dataset.oneBottlePriceInCents) || 0,
    threeBottlePriceInCents: parseInt(priceDataElement.dataset.threeBottlePriceInCents) || 0,
    sixBottlePriceInCents: parseInt(priceDataElement.dataset.sixBottlePriceInCents) || 0
  };
}

/**
 * Helper: Get Currency Code
 */
function getCurrencyCode() {
  const priceDataElement = document.getElementById('pdp-price-data');
  if (!priceDataElement) return 'USD';
  return priceDataElement.dataset.currencyCode || 'USD';
}

/**
 * Initialize Variant Selector (for non-Skio products)
 */
function initializeVariantSelector() {
  // Check if we have non-Skio variant selector (products without Skio integration)
  const skioComponent = document.querySelector('skio-plan-picker');
  if (skioComponent) {
    // This is a Skio product, skip variant selector initialization
    return;
  }
  
  const variantOptions = document.querySelectorAll(
    '.pdp-details__selection-option[data-variant-id][data-type="quantity"]:not([data-quantity])'
  );
  const selectedVariantSpan = document.querySelector('#selectedVariant');
  
  if (!variantOptions.length) {
    return;
  }
  
  // Set first available variant as active and update prices
  const firstActiveVariant = document.querySelector(
    '.pdp-details__selection-option--active[data-variant-id]:not([data-quantity])'
  );
  if (firstActiveVariant) {
    updateMainPriceForVariant(firstActiveVariant);
    updateAddToCartButtonForVariant(firstActiveVariant);
    triggerBoosterVariantSelect(firstActiveVariant.dataset.variantId);
  }
  
  // Handle variant selection clicks
  variantOptions.forEach(option => {
    option.addEventListener('click', function(e) {
      e.preventDefault();
      e.stopPropagation();
      
      if (this.disabled || this.dataset.available === 'false') {
        return;
      }
      
      // Remove active class from all variant options
      variantOptions.forEach(opt => opt.classList.remove('pdp-details__selection-option--active'));
      
      // Add active class to clicked option
      this.classList.add('pdp-details__selection-option--active');
      
      // Update selected variant text
      if (selectedVariantSpan) {
        const variantText = this.querySelector('.pdp-details__selection-text');
        if (variantText) {
          selectedVariantSpan.textContent = variantText.textContent.trim();
        }
      }
      
      // Update main price display
      updateMainPriceForVariant(this);
      
      // Update add-to-cart button
      updateAddToCartButtonForVariant(this);
      
      // Update add-to-cart button data attributes
      const addToCartBtns = document.querySelectorAll('.add-to-cart-btn');
      const variantId = this.dataset.variantId;
      addToCartBtns.forEach(btn => {
        btn.setAttribute('data-variant-id', variantId);
      });
      triggerBoosterVariantSelect(variantId);
    });
  });
}

/**
 * Update Main Price Display for Selected Variant (non-Skio products)
 */
function updateMainPriceForVariant(variantOption) {
  if (!variantOption) return;
  
  const priceInCents = parseInt(variantOption.dataset.variantPrice) || 0;
  const comparePriceInCents = parseInt(variantOption.dataset.variantComparePrice) || priceInCents;
  
  const priceGroup = document.getElementById('priceGroup');
  if (!priceGroup) return;
  
  const salePrice = document.getElementById('salePrice');
  const compareElement = document.getElementById('comparePrice');
  const discountBadge = document.getElementById('discountBadge');
  const discountText = document.getElementById('discountText');
  const regularPrice = document.getElementById('regularPrice');
  
  if (comparePriceInCents > priceInCents) {
    // Show sale price with compare price
    const discountPercentage = Math.round(((comparePriceInCents - priceInCents) / comparePriceInCents) * 100);
    
    if (salePrice) {
      salePrice.textContent = formatPrice(priceInCents);
      salePrice.style.display = '';
    } else {
      const newSalePrice = document.createElement('span');
      newSalePrice.className = 'pdp-details__price pdp-details__price--sale';
      newSalePrice.id = 'salePrice';
      newSalePrice.textContent = formatPrice(priceInCents);
      priceGroup.insertBefore(newSalePrice, priceGroup.firstChild);
    }
    
    if (compareElement) {
      compareElement.textContent = formatPrice(comparePriceInCents);
      compareElement.style.display = '';
    } else {
      const newComparePrice = document.createElement('span');
      newComparePrice.className = 'pdp-details__price pdp-details__price--compare';
      newComparePrice.id = 'comparePrice';
      newComparePrice.textContent = formatPrice(comparePriceInCents);
      const targetSalePrice = document.getElementById('salePrice');
      if (targetSalePrice && targetSalePrice.nextSibling) {
        priceGroup.insertBefore(newComparePrice, targetSalePrice.nextSibling);
      } else {
        priceGroup.appendChild(newComparePrice);
      }
    }
    
    if (discountText) {
      discountText.textContent = discountPercentage + '% off';
      if (discountBadge) discountBadge.style.display = '';
    } else if (discountBadge) {
      const newDiscountText = document.createElement('span');
      newDiscountText.className = 'pdp-details__discount-text';
      newDiscountText.id = 'discountText';
      newDiscountText.textContent = discountPercentage + '% off';
      discountBadge.appendChild(newDiscountText);
      discountBadge.style.display = '';
    } else {
      const newBadge = document.createElement('div');
      newBadge.className = 'pdp-details__discount-badge';
      newBadge.id = 'discountBadge';
      const newDiscountText = document.createElement('span');
      newDiscountText.className = 'pdp-details__discount-text';
      newDiscountText.id = 'discountText';
      newDiscountText.textContent = discountPercentage + '% off';
      newBadge.appendChild(newDiscountText);
      priceGroup.appendChild(newBadge);
    }
    
    if (regularPrice) {
      regularPrice.style.display = 'none';
    }
  } else {
    // Show regular price only
    if (regularPrice) {
      regularPrice.textContent = formatPrice(priceInCents);
      regularPrice.style.display = '';
    } else {
      const newRegularPrice = document.createElement('span');
      newRegularPrice.className = 'pdp-details__price';
      newRegularPrice.id = 'regularPrice';
      newRegularPrice.textContent = formatPrice(priceInCents);
      priceGroup.appendChild(newRegularPrice);
    }
    
    if (salePrice) salePrice.style.display = 'none';
    if (compareElement) compareElement.style.display = 'none';
    if (discountBadge) discountBadge.style.display = 'none';
  }
}

/**
 * Update Add to Cart Button for Selected Variant (non-Skio products)
 */
function updateAddToCartButtonForVariant(variantOption) {
  if (!variantOption) return;
  
  const priceInCents = parseInt(variantOption.dataset.variantPrice) || 0;
  const priceFormatted = formatPrice(priceInCents);
  
  const addToCartTextElements = document.querySelectorAll('#add-to-cart-text, #add-to-cart-text-mobile');
  
  if (!addToCartTextElements.length) {
    return;
  }
  
  const buttonText = `Add to Cart • ${priceFormatted}`;
  
  addToCartTextElements.forEach(textElement => {
    textElement.textContent = buttonText;
  });
}

